import React, { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";

const HorizontalBarChart = ({
  chartdata,
  height = "450px",
  barColor = "#008FFB",
  xAxisTitle = "Value",
  tooltipUnit = "",
  decimalPoints = 0
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    if (!chartdata?.categories || !chartdata?.series) return;

    // ✅ STEP 1: Sort data by value (highest first)
    const combined = chartdata.categories.map((cat, idx) => ({
      category: cat,
      value: chartdata.series[idx]
    }));
    
    combined.sort((a, b) => b.value - a.value);
    
    const sortedCategories = combined.map(item => item.category);
    const sortedSeries = combined.map(item => item.value);

    // ✅ STEP 2: Calculate dynamic height
    const rowHeight = 65;
    const numberOfItems = sortedCategories.length;
    const internalHeight = Math.max(400, numberOfItems * rowHeight);

    const options = {
      series: [
        {
          name: xAxisTitle,
          data: sortedSeries,
        },
      ],
      chart: {
        type: "bar",
        height: internalHeight,
        toolbar: { show: false },
        animations: { enabled: true, speed: 800 },
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 5,
          barHeight: "65%", // ✅ Thicker bars for better label visibility
          dataLabels: { 
            position: "center" // ✅ Changed to center - labels inside bars
          },
        },
      },
      colors: [barColor],
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (val === 0) return "";
          return Number(val).toFixed(decimalPoints);
        },
        offsetX: 0, // ✅ No offset - centered in bar
        style: {
          fontSize: "13px", // ✅ Slightly larger for readability
          colors: ["#ffffff"], // ✅ WHITE text for contrast against blue bars
          fontWeight: "700", // ✅ Bold for clarity
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          opacity: 0.3 // ✅ Subtle shadow for better text visibility
        }
      },
      xaxis: {
        categories: sortedCategories,
        position: "top",
        title: {
          text: xAxisTitle,
          style: { 
            fontSize: "14px", 
            fontWeight: 600,
            color: "#263238"
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            fontSize: "11px",
            colors: ["#607D8B"]
          }
        }
      },
      yaxis: {
        labels: {
          show: true,
          align: "right",
          minWidth: 220, // ✅ More space for company names
          maxWidth: 380,
          style: { 
            fontSize: "13px",
            colors: sortedSeries.map((val, idx) => {
              // ✅ Top 3 companies darker and bolder
              return idx < 3 ? "#0d47a1" : "#424242";
            }),
            fontWeight: sortedSeries.map((val, idx) => {
              return idx < 3 ? "700" : "500";
            })
          },
          formatter: (value) => {
            if (!value) return "";
            // ✅ Better truncation with proper spacing
            return value.length > 45 ? value.substring(0, 42) + "..." : value;
          },
        },
      },
      grid: {
        show: true,
        borderColor: "#e8eaf6",
        strokeDashArray: 4,
        padding: { 
          top: 0, 
          right: 30,
          bottom: 0, 
          left: 15 
        },
        xaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        enabled: true,
        theme: "dark",
        y: {
          formatter: (val) => {
            return `${Number(val).toFixed(decimalPoints)} ${tooltipUnit}`;
          },
        },
        style: {
          fontSize: "13px"
        }
      },
    };

    chartInstance.current = new ApexCharts(chartRef.current, options);
    chartInstance.current.render();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartdata, barColor, xAxisTitle, tooltipUnit, decimalPoints]);

  return (
    <div
      style={{
        height: height,
        overflowY: "auto",
        overflowX: "hidden",
        border: "1px solid #e3f2fd",
        borderRadius: "6px",
        paddingRight: "8px",
        backgroundColor: "#fafafa"
      }}
    >
      {!chartdata ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "#757575", fontSize: "14px" }}>No data available</p>
        </div>
      ) : (
        <div ref={chartRef} style={{ width: "100%" }}></div>
      )}
    </div>
  );
};

export default HorizontalBarChart;